/*  MUI  */
import { DeleteForever, PlayArrow } from '@mui/icons-material';
import { Box, Button, Grid, Paper, TableCell } from '@mui/material';

/*  Shared  */
import { getDateTime } from 'application/Shared/Components/Core';
import { ManagedTable } from 'application/Shared/Components/Table';
import { FontSizes } from 'application/Shared/Constants';
import { Notify, Response, Transition } from 'application/Shared/Helpers';
import Core from 'application/Shared/Helpers/Functions';
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import i18n from 'i18n';

/*  Objects  */
import { User, UserIconAndName } from 'objects/User';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

/*  Components  */
import { ReportSummary } from './ReportSummary';

const headers = [
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{i18n.t('Common.name')}</strong>,
        width: '20%'
    },
    {
        align: 'left',
        field: 'createdby',
        renderHeader: <strong>{i18n.t('Common.created_by')}</strong>,
        width: '20%'
    },
    {
        align: 'left',
        field: 'createdfor',
        renderHeader: <strong>{i18n.t('Common.created_for')}</strong>,
        width: '20%'
    },
    {
        align: 'left',
        field: 'configuration',
        renderHeader: <strong>{i18n.t('Common.configuration')}</strong>,
        width: '20%'
    },
    {
        align: 'right',
        field: 'actions',
        renderHeader: <strong>{i18n.t('Common.actions')}</strong>,
        width: '20%'
    }
];

const searchFields = ['name', 'period', 'email'];

class Scheduled extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loaded: false,
            reports: [],
            viewReport: {
                open: false,
                params: {}
            }
        };

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = async () => {
        const { match } = this.props;
        const { clientId } = match.params;

        /* api params */
        const params = { period: '*' };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/scheduledReport', {
                cancelToken: this.requests.token(),
                params
            });
            if (!Response.hasData(response)) {
                this.setState({ loaded: true });
                return;
            }

            const reports = Response.getData(response);

            this.setState({ loaded: true, reports });
        } catch (e) {
            this.setState({ loaded: true });
            debug.e('Error getting Scheduled Reports', e);
        }
    };

    unload = () => {
        this.requests.cancel();
    };

    viewReport = (row) => {
        this.setState({ viewReport: { open: true, params: row.params } });
    };

    deleteReport = async (row) => {
        const { match, t } = this.props;
        const { clientId } = match.params;
        const { reports } = this.state;
        const { id } = row;

        /* api params */
        const params = {};
        if (clientId) {
            params.platformTenantId = clientId;
        }

        try {
            await axios.delete(`/scheduledReport/${id}`, { params });
            Notify.s(t('Reports.ScheduledReport.success_remove'));

            this.setState({
                reports: reports.filter((r) => r.id !== row.id)
            });
        } catch (e) {
            debug.e('Error deleting Scheduled Report', e);
            Notify.e(t('Reports.ScheduledReport.error_removing_report'));
        }
    };

    renderModal = () => {
        const { t } = this.props;
        const { viewReport } = this.state;

        return (
            <Modal
                onClose={() =>
                    this.setState({
                        viewReport: {
                            ...viewReport,
                            open: false
                        }
                    })
                }
                open={viewReport.open}
                aria-labelledby="simple-dialog-title"
                TransitionComponent={Transition}
                title={t('Reports.ScheduledReport.report_configuration')}
            >
                <ModalBody>
                    <ReportSummary
                        noDates
                        expanded
                        hideReportSelection
                        reportSetup={viewReport.params}
                        title=""
                    />
                </ModalBody>
                <ModalFooter>
                    <Button
                        data-trackid="reports_scheduledReport_modal_close"
                        onClick={() =>
                            this.setState({
                                viewReport: {
                                    ...viewReport,
                                    open: false
                                }
                            })
                        }
                        color="primary"
                        variant="outlined"
                    >
                        {t('Common.close')}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    };

    renderRow = (row) => {
        const { runReport, t } = this.props;

        return (
            <React.Fragment key={row.id}>
                <TableCell>
                    {row.name}
                    <br />
                    <small>
                        {t('Common.created', {
                            date: getDateTime({
                                date: row.dateCreated,
                                noTz: true
                            })
                        })}
                    </small>
                </TableCell>
                <TableCell>
                    <User includeClients id={row.creatorPlatformUserId}>
                        <UserIconAndName />
                    </User>
                </TableCell>
                <TableCell>
                    {row?.email ? (
                        row.email
                    ) : (
                        <User includeClients id={row.emailPlatformUserId}>
                            <UserIconAndName />
                        </User>
                    )}
                </TableCell>
                <TableCell>
                    {Core.ucfirst(row.period)} / {Core.ucfirst(row.params.type)}
                    <br />
                    <Box
                        as="a"
                        onClick={() => this.viewReport(row)}
                        className="link underline"
                        aria-hidden="true"
                    >
                        <small>
                            {t('Reports.ScheduledReport.view_full_report')}
                        </small>
                    </Box>
                </TableCell>
                <TableCell align="right">
                    <Button
                        data-trackid="reports_scheduledReport_runReport"
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            runReport(row.params, false);
                        }}
                        style={{
                            marginRight: '4px',
                            minWidth: '32px',
                            paddingLeft: 0,
                            paddingRight: 0
                        }}
                    >
                        <PlayArrow
                            style={{
                                fontSize: FontSizes[3]
                            }}
                        />
                    </Button>
                    <Button
                        data-trackid="reports_scheduledReport_deleteReport"
                        variant="outlined"
                        color="primary"
                        onClick={() => this.deleteReport(row)}
                        style={{
                            minWidth: '32px',
                            paddingLeft: 0,
                            paddingRight: 0
                        }}
                    >
                        <DeleteForever
                            style={{
                                fontSize: FontSizes[3]
                            }}
                        />
                    </Button>
                </TableCell>
            </React.Fragment>
        );
    };

    render() {
        const { loaded, reports, viewReport } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <Paper
                elevation={0}
                variant="outlined"
                sx={{ marginBottom: '32px' }}
                className="no-print"
            >
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <ManagedTable
                            headers={headers}
                            renderBody={this.renderRow}
                            renderBodyKey="id"
                            renderFooter
                            rows={reports}
                            rowsPerPage={10}
                            searchEnabled
                            searchFields={searchFields}
                            sortDirection="ASC"
                            sortFieldType="string"
                            sortKey="name"
                        />
                        {viewReport.open && this.renderModal()}
                    </Grid>
                </Grid>
            </Paper>
        );
    }
}
export default withRouter(withTranslation()(Scheduled));
